import { Box, MenuItem, Select, Typography, useTheme } from "@mui/material";
import { IconButton } from "../atoms/IconButton";

type JSONTablePaginationProps = {
  /** The current page number. Starts from 1 */
  page: number;
  /** Number of rows to display per page */
  rowsPerPage: number;
  /** Total number of rows present */
  totalCount: number | undefined;
  /** Callback to change the number of rows per page */
  setRowsPerPage: (value: number) => void;
  /** Callback to change the page number */
  setPage: (newPageNumber: number) => void;
  /** Show the number of rows menu */
  showRowsMenu?: boolean;
  /** Rows Title */
  rowsTitle?: string;
  /** Rows Menu */
  rowsMenu?: number[];
};

export const JSONTablePagination = (props: JSONTablePaginationProps) => {
  const {
    page,
    rowsPerPage,
    setRowsPerPage,
    setPage,
    totalCount,
    showRowsMenu = true,
    rowsTitle = "Rows",
    rowsMenu = [5, 10, 20],
  } = props;
  const totalPages = Math.ceil((totalCount ?? 0) / rowsPerPage);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const changeRowsPerPage = (newRowsPerPage: number) => {
    const newTotalPages = Math.ceil((totalCount ?? 0) / newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    // Prevent the page number going out of bounds
    if (page > newTotalPages) {
      setPage(Math.max(1, newTotalPages));
    }
  };
  // hide self when there are no pages
  if (totalPages === 0 && typeof totalCount !== "undefined") {
    return null;
  }
  const totalCountLabel = (totalCount ?? 0) > 1 ? `${totalCount} results` : `1 result`;

  const getPageNumbers = () => {
    const pageNumbers = [];

    pageNumbers.push(1);

    let startPage = Math.max(2, page - 2);
    let endPage = Math.min(totalPages - 1, page + 2);

    if (startPage > 2) {
      pageNumbers.push("ellipsis1");
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages - 1) {
      pageNumbers.push("ellipsis2");
    }

    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const scrollToTop = () => {
    const table = document.getElementById("json-table-view");
    if (table) {
      table.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="body2">{totalCountLabel}</Typography>
      {totalPages > 1 && (
        <Box display="flex" alignItems="center" gap="8px" mx="auto">
          {!(page === 1) && (
            <IconButton
              variant="left-arrow"
              onClick={() => {
                setPage(page - 1);
                scrollToTop();
              }}
              width="24px"
              height="24px"
              disabled={page === 1}
              borderRadius="50%"
            />
          )}
          {getPageNumbers().map((pageNum, index) => {
            if (pageNum === "ellipsis1" || pageNum === "ellipsis2") {
              return (
                <Typography
                  key={`ellipsis-${index}`}
                  variant="body2"
                  color={theme.palette.custom.grey[600]}
                >
                  ...
                </Typography>
              );
            }
            return (
              <Box
                key={`page-${pageNum}`}
                sx={{
                  cursor: "pointer",
                  minWidth: "34px",
                  height: "34px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor:
                    page === pageNum ? theme.palette.custom.blueTypography : "transparent",
                  "&:hover": {
                    backgroundColor:
                      page === pageNum
                        ? theme.palette.custom.blueTypography
                        : theme.palette.custom.secondaryBackground,
                  },
                }}
                onClick={() => {
                  setPage(pageNum as number);
                  scrollToTop();
                }}
              >
                <Typography
                  variant="h4"
                  color={
                    page === pageNum
                      ? isDarkMode
                        ? theme.palette.custom.primaryTypography
                        : theme.palette.common.white
                      : theme.palette.custom.primaryTypography
                  }
                >
                  {pageNum}
                </Typography>
              </Box>
            );
          })}
          {!(page === totalPages) && (
            <IconButton
              variant="right-arrow"
              onClick={() => {
                setPage(page + 1);
                scrollToTop();
              }}
              width="24px"
              height="24px"
              disabled={page === totalPages}
              borderRadius="50%"
            />
          )}
        </Box>
      )}
      {showRowsMenu && (
        <Select
          value={rowsPerPage}
          sx={{ padding: 0, height: "35px", borderRadius: "3px" }}
          onChange={(event) => changeRowsPerPage(event.target.value as number)}
        >
          {rowsMenu.map((value) => (
            <MenuItem key={value} value={value}>{`${rowsTitle}: ${value}`}</MenuItem>
          ))}
        </Select>
      )}
    </Box>
  );
};
